import { HttpService } from "@services/http";
import {
  prepareErrorResponse,
  prepareResponseObject,
} from "@services/http/response";
import { RESPONSE_TYPES } from "@constants/response-types";
import axios from "axios";
import { getBaseUrl } from "@root/store/selectors/features";
import { store } from "@root/store";

/**
 * Document Service API signatures
 * - createDocument: POST /api/documents, form-data (file)
 * - createDocumentWithProgress: POST /api/documents with progress tracking
 * - getAllDocuments: GET /api/documents, optional form-data (file)
 * - getDocumentById: GET /api/documents/:id
 * - processDocument: POST /api/documents/upload-to-ai, { id }
 */

export class DocumentsService extends HttpService {
  baseUrl = getBaseUrl(store.getState());

  createDocument = async (formData: FormData): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/documents`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  createDocumentWithProgress = async (
    formData: FormData,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal
  ): Promise<TObject> => {
    try {
      const headers = await this.getHeaders({
        headers: { "Content-Type": "multipart/form-data" },
      });

      const apiResponse = await axios.post(
        `${this.baseUrl}/api/documents`,
        formData,
        {
          headers,
          timeout: this.getTimeOutDuration(),
          signal,
          onUploadProgress: (progressEvent: any) => {
            if (progressEvent.total && onProgress) {
              const progress = Math.round(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              onProgress(progress);
            }
          },
        }
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getAllDocuments = async (payload?: any): Promise<TObject> => {
    try {
      // let apiResponse;
      // apiResponse = await this.get(`${this.baseUrl}/api/documents`, payload);
      // return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getDocumentById = async (id: string | number): Promise<TObject> => {
    try {
      const apiResponse = await this.get(`${this.baseUrl}/api/documents/${id}`);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  processDocument = async (payload: {
    id: string | number;
  }): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/documents/upload-to-ai`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  processDocumentReview = async (payload: {
    id: string | number;
  }): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/documents/upload-to-ai-review`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  processDocumentLegal = async (payload: {
    id: string | number;
  }): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/documents/excel-auto-complete`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  processMaterialComparison = async (payload: any): Promise<TObject> => {
    try {
      const apiResponse = await this.post(
        `${this.baseUrl}/api/documents/upload-to-ai-finishing-material-request`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getSuggestedQuestions = async (payload?: any): Promise<TObject> => {
    try {
      let apiResponse;
      apiResponse = await this.get(
        `${this.baseUrl}/api/suggestion-questions`,
        payload
      );
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };

  getFaqs = async (payload?: any): Promise<TObject> => {
    try {
      let apiResponse;
      apiResponse = await this.get(`${this.baseUrl}/api/faqs`, payload);
      return prepareResponseObject(apiResponse, RESPONSE_TYPES.SUCCESS);
    } catch (error) {
      return prepareErrorResponse(error);
    }
  };
}
